/**
 * Dev fallback for the YaaS Client Console **Portfolio** (Dashboard) while
 * the client reward, Program Position, and distribution-history endpoints are
 * unavailable in local development.
 *
 * Persona: **Nimbus** — a consumer crypto neobank / DeFi savings app that
 * integrates Solomon YaaS to stream yield onto its users' USDv balances.
 * The positions below are the Program Surfaces Nimbus routes its users' USDv
 * into; earnings and distributions are the rewards streamed back.
 *
 * Each constant matches the EXACT shape of the corresponding real endpoint
 * response so swapping to live data is a no-op. Mirrors the mock-fallback
 * pattern in `hooks/useProgramPolicies.ts`. PLACEHOLDER until the backend
 * exists.
 *
 * All amounts are USDv (1:1 USD by construction).
 */

import { type ClientPositionView, type SavedDestination, YaasPositionStatus } from "@solomon-apps/shared";

import type { ClientEarningsResponse, DailyEarning, DistributionItem } from "@/services/clientPositions";

const DAY_MS = 24 * 60 * 60 * 1000;
const BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Deterministic base58 string seeded by `seed` (FNV-1a + xorshift). */
function pseudoBase58(seed: string, length: number): string {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    let out = "";
    while (out.length < length) {
        h ^= h << 13;
        h >>>= 0;
        h ^= h >> 17;
        h ^= h << 5;
        h >>>= 0;
        out += BASE58[h % 58];
    }
    return out;
}

/** Start-of-day UTC for a day offset from today. */
function dayMs(offsetFromToday: number): number {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d.getTime() + offsetFromToday * DAY_MS;
}

const NOW_ISO = "2026-06-08T00:00:00.000Z";

/* ── Positions ───────────────────────────────────────────────────────────
 * Six Program Surfaces Nimbus routes user USDv into. `surfaceType` is "POOL"/"VAULT"
 * (never TREASURY/WALLET) so the display layer uses `manualUsdvAmount` and
 * never tries to fetch a live onchain balance. Principal sums to 1,284,500.
 */

interface SeedPosition {
    label: string;
    surfaceName: string;
    surfaceType: string;
    /** Instrument the position holds, e.g. "USDv-USDC". */
    instrument: string;
    /** Surface Type label, e.g. "Meteora DAMM". */
    surfaceTypeLabel: string;
    principal: number;
    trailingApyPct: number;
    totalRewards: number;
    status: YaasPositionStatus;
}

const SEED_POSITIONS: SeedPosition[] = [
    {
        label: "Nimbus Savings — Core",
        surfaceName: "Meteora USDv-USDC Pool",
        surfaceType: "POOL",
        instrument: "USDv-USDC",
        surfaceTypeLabel: "Meteora DAMM",
        principal: 540_000,
        trailingApyPct: 9.42,
        totalRewards: 21_480.5,
        status: YaasPositionStatus.Active
    },
    {
        label: "Nimbus Savings — Growth",
        surfaceName: "Kamino USDv Lend Vault",
        surfaceType: "VAULT",
        instrument: "USDv Lending",
        surfaceTypeLabel: "Kamino Lend",
        principal: 312_000,
        trailingApyPct: 8.74,
        totalRewards: 12_905.12,
        status: YaasPositionStatus.Active
    },
    {
        label: "Nimbus Direct Deposit Pool",
        surfaceName: "Drift USDv Strategy Vault",
        surfaceType: "VAULT",
        instrument: "USDv Lending",
        surfaceTypeLabel: "Drift Strategy",
        principal: 205_500,
        trailingApyPct: 7.91,
        totalRewards: 8_142.33,
        status: YaasPositionStatus.Active
    },
    {
        label: "Nimbus Loyalty Reserve",
        surfaceName: "Marginfi USDv Bank",
        surfaceType: "POOL",
        instrument: "USDv Lending",
        surfaceTypeLabel: "Marginfi Lend",
        principal: 142_000,
        trailingApyPct: 6.88,
        totalRewards: 4_201.7,
        status: YaasPositionStatus.Active
    },
    {
        label: "Nimbus Treasury Buffer",
        surfaceName: "Solomon USDv Reserve Pool",
        surfaceType: "POOL",
        instrument: "USDv Lending",
        surfaceTypeLabel: "Solomon Reserve",
        principal: 85_000,
        trailingApyPct: 6.5,
        totalRewards: 2_190.35,
        status: YaasPositionStatus.Active
    },
    {
        // A position the issuer paused — exercises the "disabled" muted row.
        label: "Nimbus Pilot Pool",
        surfaceName: "Orca USDv Whirlpool",
        surfaceType: "POOL",
        instrument: "USDv-USDC",
        surfaceTypeLabel: "Orca Whirlpool",
        principal: 0,
        trailingApyPct: 0,
        totalRewards: 0,
        status: YaasPositionStatus.Disabled
    }
];

function buildPosition(seed: SeedPosition, index: number): ClientPositionView {
    const idTail = pseudoBase58(`nimbus-pos-${index}`, 22).toUpperCase();
    const surfaceAddress = pseudoBase58(`nimbus-surface-${index}`, 44);
    const accountAddress = pseudoBase58(`nimbus-acct-${index}`, 44);
    const destinationAddress = pseudoBase58(`nimbus-dest-${index}`, 44);
    const isDisabled = seed.status === YaasPositionStatus.Disabled;
    return {
        uuid: `pos_01HX9K4A${idTail}`,
        status: seed.status,
        manualUsdvAmount: seed.principal.toFixed(2),
        totalRewards: seed.totalRewards.toFixed(2),
        rewardsDestinationAddress: destinationAddress,
        rejectionReason: null,
        rejectedAt: null,
        suspensionReason: null,
        suspendedAt: null,
        disabledAt: isDisabled ? "2026-04-12T00:00:00.000Z" : null,
        hiddenAt: null,
        verifiedAt: "2025-09-01T00:00:00.000Z",
        programSurface: {
            uuid: `program_surface_${pseudoBase58(`nimbus-surfaceuuid-${index}`, 20)}`,
            name: seed.surfaceName,
            type: seed.surfaceType,
            product: seed.instrument,
            surface: seed.surfaceTypeLabel,
            address: surfaceAddress,
            trailingApyPct: seed.trailingApyPct > 0 ? seed.trailingApyPct.toFixed(2) : null
        },
        account: {
            uuid: `acct_${pseudoBase58(`nimbus-acctuuid-${index}`, 20)}`,
            label: seed.label,
            address: accountAddress
        },
        createdAt: "2025-09-01T00:00:00.000Z",
        updatedAt: NOW_ISO
    };
}

export const MOCK_CLIENT_POSITIONS: ClientPositionView[] = SEED_POSITIONS.map(buildPosition);

/* ── Portfolio key-stats (derived) ──────────────────────────────────────
 * Single source of truth for the four Portfolio key stats so the preview
 * numbers stay consistent with the positions + earnings below.
 */

/** Total USDv principal held across active positions. */
export const TOTAL_USDV_POSITIONS = SEED_POSITIONS.reduce((sum, p) => sum + p.principal, 0);

/** Total USDv yield earned to date = Σ position lifetime rewards. */
export const TOTAL_USDV_EARNED = Math.round(SEED_POSITIONS.reduce((sum, p) => sum + p.totalRewards, 0));

/** USDv accrued toward the next distribution but not yet distributed. */
export const TOTAL_USDV_PENDING = 3_140;

/** Number of positions (all rows, including the paused one). */
export const TOTAL_POSITIONS = SEED_POSITIONS.length;

/* ── Earnings ────────────────────────────────────────────────────────────
 * ~1 year of daily cumulative earnings. Portfolio APY drifts near ~8.6%.
 * The cumulative series ends at TOTAL_USDV_EARNED so the headline reconciles.
 */

const N_DAYS = 365;
const PORTFOLIO_BALANCE = TOTAL_USDV_POSITIONS;

function buildDaily(): DailyEarning[] {
    const raw: { apy: number; earn: number }[] = [];
    let rawTotal = 0;
    for (let i = 0; i < N_DAYS; i++) {
        const apy = 8.6 + Math.sin(i / 60) * 0.4 + Math.sin(i / 7) * 0.12 + Math.sin(i / 19) * 0.08;
        const earn = (PORTFOLIO_BALANCE * (apy / 100)) / 365;
        raw.push({ apy, earn });
        rawTotal += earn;
    }
    // Scale daily earnings so the cumulative total lands on TOTAL_USDV_EARNED.
    const scale = rawTotal > 0 ? TOTAL_USDV_EARNED / rawTotal : 1;
    const daily: DailyEarning[] = [];
    let cumulative = 0;
    for (let i = 0; i < N_DAYS; i++) {
        cumulative += raw[i].earn * scale;
        daily.push({
            timestampMs: dayMs(i - (N_DAYS - 1)),
            cumulativeEarnedUsdv: Math.round(cumulative * 100) / 100,
            apyPct: Math.round(raw[i].apy * 100) / 100,
            dailyBalanceUsdv: PORTFOLIO_BALANCE
        });
    }
    return daily;
}

/**
 * Per-position cumulative earnings series — PLACEHOLDER.
 *
 * The real `/api/org/client/earnings?position=<uuid>` endpoint will return a
 * position-scoped `DailyEarning[]`. Until it exists, we reuse the portfolio
 * curve's *shape* (same APY drift / day grid) and rescale it so the cumulative
 * total lands on the position's own lifetime `totalRewards`. Balance and APY
 * fields are filled from the position so the chart's benchmark math and APY
 * view stay self-consistent. Mirrors `buildDaily()`.
 */
export function buildPositionDaily(position: ClientPositionView): DailyEarning[] {
    const positionEarned = Number(position.totalRewards);
    const balance = Number(position.manualUsdvAmount);
    const apyBase =
        position.programSurface.trailingApyPct !== null && position.programSurface.trailingApyPct !== undefined
            ? Number(position.programSurface.trailingApyPct)
            : 8.6;

    const raw: { apy: number; earn: number }[] = [];
    let rawTotal = 0;
    for (let i = 0; i < N_DAYS; i++) {
        const apy = apyBase + Math.sin(i / 60) * 0.4 + Math.sin(i / 7) * 0.12 + Math.sin(i / 19) * 0.08;
        const earn = (balance * (apy / 100)) / 365;
        raw.push({ apy, earn });
        rawTotal += earn;
    }
    // Scale daily earnings so the cumulative total lands on the position's
    // lifetime rewards. A zero-reward (e.g. disabled) position yields a flat
    // $0 line, which is the correct read.
    const scale = rawTotal > 0 ? positionEarned / rawTotal : 0;
    const daily: DailyEarning[] = [];
    let cumulative = 0;
    for (let i = 0; i < N_DAYS; i++) {
        cumulative += raw[i].earn * scale;
        daily.push({
            timestampMs: dayMs(i - (N_DAYS - 1)),
            cumulativeEarnedUsdv: Math.round(cumulative * 100) / 100,
            apyPct: Math.round(raw[i].apy * 100) / 100,
            dailyBalanceUsdv: balance
        });
    }
    return daily;
}

/**
 * PLACEHOLDER series for one holding: the underlying positions' series with
 * earnings summed (rewards are per position) and the balance counted once
 * (the positions report the same funds, so the max, never the sum).
 */
export function buildHoldingDaily(positions: readonly ClientPositionView[]): DailyEarning[] {
    const series = positions.map(buildPositionDaily);
    const [first, ...rest] = series;
    if (!first) return [];
    return first.map((day, i) => ({
        ...day,
        cumulativeEarnedUsdv:
            Math.round(
                rest.reduce((sum, s) => sum + (s[i]?.cumulativeEarnedUsdv ?? 0), day.cumulativeEarnedUsdv) * 100
            ) / 100,
        dailyBalanceUsdv: Math.max(day.dailyBalanceUsdv, ...rest.map((s) => s[i]?.dailyBalanceUsdv ?? 0))
    }));
}

const DAILY = buildDaily();
const TRAILING_APY = DAILY[DAILY.length - 1]?.apyPct ?? 8.6;
const APY_30D_AGO = DAILY[DAILY.length - 31]?.apyPct ?? TRAILING_APY;
const EARNED_30D =
    (DAILY[DAILY.length - 1]?.cumulativeEarnedUsdv ?? 0) - (DAILY[DAILY.length - 31]?.cumulativeEarnedUsdv ?? 0);

export const MOCK_CLIENT_EARNINGS: ClientEarningsResponse = {
    daily: DAILY,
    headline: {
        trailingApyPct: TRAILING_APY,
        apyDeltaPct: Math.round((TRAILING_APY - APY_30D_AGO) * 100) / 100,
        earnedWindowUsdv: Math.round(EARNED_30D * 100) / 100,
        earnedTotalUsdv: TOTAL_USDV_EARNED,
        earnedDeltaUsdv: Math.round(EARNED_30D * 100) / 100
    },
    snapshotAt: NOW_ISO
};

/* ── Distributions ───────────────────────────────────────────────────────
 * Weekly distributions over the last ~26 weeks, cycling across the active
 * Program Surfaces. Ordered newest-first (the table further sorts on display).
 */

const ACTIVE_SEEDS = SEED_POSITIONS.filter((p) => p.status === YaasPositionStatus.Active && p.principal > 0);
const N_WEEKS = 26;

function seededUnit(seed: number): number {
    let t = (seed + 0x6d2b79f5) | 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function buildDistributions(): DistributionItem[] {
    const items: DistributionItem[] = [];
    for (let w = 0; w < N_WEEKS; w++) {
        const seed = ACTIVE_SEEDS[w % ACTIVE_SEEDS.length];
        const offsetDays = -w * 7;
        // Weekly yield ≈ principal × apy/100 × 7/365, with mild jitter.
        const base = (seed.principal * (seed.trailingApyPct / 100) * 7) / 365;
        const jitter = 0.85 + seededUnit(w * 2654435761) * 0.3;
        const amount = Math.round(base * jitter * 100) / 100;
        items.push({
            uuid: `dist_${pseudoBase58(`nimbus-dist-${w}`, 24)}`,
            distributionDate: new Date(dayMs(offsetDays)).toISOString(),
            amountUsdv: amount,
            pool: seed.surfaceName,
            sourceWallet: pseudoBase58(`nimbus-src-${seed.surfaceName}`, 44),
            destinationWallet: pseudoBase58(`nimbus-dst-${seed.surfaceName}`, 44),
            txSignature: pseudoBase58(`nimbus-tx-${w}`, 88)
        });
    }
    return items;
}

export const MOCK_CLIENT_DISTRIBUTIONS: DistributionItem[] = buildDistributions();

/* ── Saved reward destinations ───────────────────────────────────────────
 * The org's address book (Reward Destinations page + the position reward-
 * destination picker). The first few reuse a live position's destination
 * address so the page shows non-zero "Positions Using"; the last is saved
 * but not yet routed to (Positions Using = 0). PLACEHOLDER until the
 * /api/org/client/saved-destinations endpoint exists.
 */
const SAVED_DESTINATION_NAMES = ["Treasury Wallet", "Operations Wallet", "Cold Storage"];

const ROUTED_DESTINATIONS: string[] = MOCK_CLIENT_POSITIONS.map((p) => p.rewardsDestinationAddress)
    .filter((address): address is string => Boolean(address))
    .slice(0, SAVED_DESTINATION_NAMES.length);

export const MOCK_SAVED_DESTINATIONS: SavedDestination[] = [
    ...ROUTED_DESTINATIONS.map((address, index) => ({
        uuid: `dest_${pseudoBase58(`nimbus-saved-${index}`, 20)}`,
        name: SAVED_DESTINATION_NAMES[index] ?? `Wallet ${index + 1}`,
        address,
        createdAt: new Date(dayMs(-30 - index * 5)).toISOString(),
        updatedAt: NOW_ISO
    })),
    {
        uuid: `dest_${pseudoBase58("nimbus-saved-unused", 20)}`,
        name: "Partner Payouts",
        address: pseudoBase58("nimbus-saved-unused-addr", 44),
        createdAt: new Date(dayMs(-3)).toISOString(),
        updatedAt: NOW_ISO
    }
];
